<script lang="ts" setup>
import stores from '@jaaluu/stores';
import { t } from '@shared/i18n';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import IconSuccessFail from './IconSuccessFail.vue';

const router = useRouter();

const { getStoryUrl, getParamsStr, getTrainUrl } = stores.course.funcs;
const { langForeign, langNative } = stores.language.state;
const { TRANS } = stores.translate.consts;
const { favoriteGroups } = stores.favorites.state;
const { curriculum, courseStatus } = stores.course.state;
const {} = stores.course.funcs;
const { courseProgress } = stores.courseProgress.state;

const emit = defineEmits<{
  (e: 'close'): void;
}>();
const loading = ref(false);

const navigateToStory = (storyIdx: number, fromContext = false) => {
  if (
    !fromContext &&
    storyIdx > 0 &&
    !courseProgress.value.stories[storyIdx - 1].trainStepsCompleted
  )
    return;
  console.log('Navigating to story:', storyIdx);
  courseStatus.value.isActive = true;
  courseStatus.value.storyIdx = storyIdx;
  courseStatus.value.trainIdx = -1;
  courseStatus.value.updateIsActive = false;
  courseStatus.value.sentenceIdx = 0;
  courseStatus.value.currentWordIdx = 0;
  const story = curriculum.value[courseStatus.value.storyIdx];
  const url = `${getStoryUrl(story.storyIdx)}`;
  router.push(url);
  emit('close');
};

// Check if a train step is locked (not selectable yet)
const isTrainStepLocked = (storyIdx: number, trainIdx: number): boolean => {
  if (storyIdx === 0 && trainIdx === 0) {
    return !courseProgress.value.stories[0].completed;
  }
  if (storyIdx > 0 && trainIdx === 0) {
    return !courseProgress.value.stories[storyIdx].completed;
  }
  return !courseProgress.value.stories?.[storyIdx].trainSteps[trainIdx - 1].completed;
};

// Navigate to a training step
const navigateToTrainStep = (storyIdx: number, trainIdx: number, fromContext = false) => {
  console.log('Navigating to training step:', storyIdx, trainIdx);
  if (!fromContext && isTrainStepLocked(storyIdx, trainIdx)) return;
  courseStatus.value.isActive = true;
  courseStatus.value.trainIdx = trainIdx;
  courseStatus.value.storyIdx = storyIdx;
  courseStatus.value.updateIsActive = false;
  courseStatus.value.sentenceIdx = 0;
  courseStatus.value.currentWordIdx = 0;
  // new step in train mode
  const trainStep =
    curriculum.value[courseStatus.value.storyIdx].trainSteps[courseStatus.value.trainIdx];
  const url = `${getTrainUrl(trainStep.type, courseStatus.value.storyIdx)}${getParamsStr(trainStep.params)}`;
  router.push(url);

  emit('close');
};

const continueToStory = (storyIdx: number) => {
  if (storyIdx > 0 && !courseProgress.value.stories[storyIdx - 1].trainStepsCompleted) return;
  const lastTrainIdx = getLastTrainIdx(storyIdx);
  if (lastTrainIdx < 0) {
    return navigateToStory(storyIdx);
  }
  navigateToTrainStep(storyIdx, lastTrainIdx);
};

const getLastTrainIdx = (storyIdx: number) => {
  const story = courseProgress.value.stories[storyIdx];
  if (!story.completed) {
    return -1;
  }
  return story.trainSteps.findIndex((ts) => !ts.completed);
};

onMounted(() => {});

const routeToFavorites = (storyIdx: number) => {
  courseStatus.value.isActive = false;
  emit('close');
  router.push(`/${langForeign.value}/favorites/words/saved?tab=course&storyIdx=${storyIdx}`);
};

const getCompletedTrainStepsCount = (storyIdx: number): number => {
  const story = courseProgress.value.stories?.[storyIdx];
  return story.trainSteps.filter((ts) => ts.completed).length;
};
</script>

<template>
  <div class="course-menu">
    <!-- Header -->
    <!-- <div class="d-flex justify-end align-center mb-1">
      <v-chip color="primary" variant="outlined">
        {{ langLevel }}
      </v-chip>
    </div> -->

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="40" />
      <div class="mt-2">{{ t('course.loading', 'Loading course...') }}</div>
    </div>

    <!-- Course content -->
    <div v-else-if="curriculum.length > 0 && langForeign && langNative">
      <v-list>
        <v-list-item
          @click="() => continueToStory(index)"
          @dblclick="() => navigateToStory(index, true)"
          v-for="(story, index) in curriculum"
          :key="index"
          :value="index"
          class="pa-0"
        >
          <!-- Story header -->
          <div
            class="main-category-header mb-6 mt-2"
            :class="{
              'disabled-step': index > 0 && !courseProgress.stories[index - 1].trainStepsCompleted
            }"
          >
            <div class="d-flex justify-space-between w-100 align-center">
              <div class="d-flex align-center">
                <div class="w-title relative">
                  <div
                    class="title ellipsis"
                    :class="{
                      'active-step': courseStatus.storyIdx === index
                    }"
                  >
                    {{ index + 1 }}. {{ story.title }}
                  </div>
                  <div>
                    <v-progress-linear
                      :model-value="courseProgress.stories[index].progress"
                      :color="courseStatus.storyIdx === index ? 'primary' : ''"
                      v-if="courseProgress.stories[index].progress > 0"
                    >
                    </v-progress-linear>
                    <div
                      class="subtitle"
                      :class="{
                        'active-step': courseStatus.storyIdx === index
                      }"
                    >
                      {{ getCompletedTrainStepsCount(index) }}/{{
                        courseProgress.stories[courseStatus.storyIdx].trainSteps.length
                      }}
                      {{ t('general.completed')
                      }}<span v-if="getLastTrainIdx(index) > -1">
                        -
                        {{
                          TRANS[langForeign].course[
                            curriculum[index].trainSteps[getLastTrainIdx(index)]?.type
                          ]
                        }}
                        -
                        {{
                          TRANS[langNative].course[
                            curriculum[index].trainSteps[getLastTrainIdx(index)]?.type
                          ]
                        }}</span
                      >
                    </div>
                  </div>
                </div>
                <IconSuccessFail
                  v-if="courseProgress.stories[index].trainStepsCompleted"
                  :show="true"
                  size="medium"
                  class="ml-4"
                />
              </div>
              <div>
                <v-badge
                  :class="{
                    opac:
                      favoriteGroups.course.words.filter((fw) => fw.storyIdx == index).length === 0
                  }"
                  id="fav-badge"
                  offset-x="5"
                  offset-y="3"
                  color="green"
                  :content="favoriteGroups.course.words.filter((fw) => fw.storyIdx == index).length"
                >
                  <v-btn
                    @click.stop="() => routeToFavorites(index)"
                    color="g"
                    variant="text"
                    size="small"
                    class=""
                    icon="mdi-translate-variant"
                  />
                </v-badge>
                <v-menu density="compact">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="small" />
                  </template>
                  <v-list v-if="langForeign && langNative">
                    <v-list-item @click="() => navigateToStory(index)" class="title-sm"
                      >{{ index + 1 }}. {{ curriculum[index].title }} -
                      {{ t('category.chapter') }}</v-list-item
                    >
                    <v-list-item
                      v-for="(trainStep, trainIdx) in story.trainSteps"
                      @click="() => navigateToTrainStep(index, trainIdx)"
                      @dblclick="() => navigateToTrainStep(index, trainIdx, true)"
                    >
                      <div
                        class="d-flex justify-space-between grab"
                        :class="{
                          'active-step':
                            courseStatus.storyIdx === index && courseStatus.trainIdx === trainIdx,
                          'disabled-step': isTrainStepLocked(index, trainIdx)
                        }"
                        @click="() => navigateToTrainStep(index, trainIdx)"
                        @dblclick="() => navigateToTrainStep(index, trainIdx, true)"
                      >
                        <div>
                          <IconSuccessFail
                            :show="courseProgress.stories[index].trainSteps[trainIdx].completed"
                            size="medium"
                            class="mb-n1 mr-2"
                          />
                          {{ index + 1 }}.{{ trainIdx + 1 }}.
                          {{ TRANS[langForeign].course[trainStep.type] }} -
                          {{ TRANS[langNative].course[trainStep.type] }}
                        </div>
                      </div>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
          <v-divider v-if="index < curriculum.length - 1"></v-divider>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<style scoped>
.course-menu {
  max-width: 1200px;
  width: 100%;
}

.step-card {
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 8px;
}

.step-card:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.course-steps {
  overflow-y: auto;
  overflow-x: hidden;
}

.subcategory-progress {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 16px;
}

.training-steps {
  margin-left: 10px;
}

.train-step-card {
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 6px;
}
.train-step-card-not-last {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.train-step-card:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.train-step-item {
  position: relative;
}

.train-step-item:before {
  content: '';
  position: absolute;
  left: -20px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.12);
}

.active-step {
  color: rgb(var(--v-theme-primary));
}
.disabled-step {
  opacity: 0.5;
  pointer-events: none;
}

.w-title {
  min-width: 200px;
  max-width: 80%;
}
@media screen and (min-width: 500px) {
  .w-title {
    min-width: 300px;
    max-width: 80%;
  }
}
</style>
